import { z } from 'zod'

/**
 * Sector-Pack-Schema (EDIH-Vertrag §4.1, Umsetzung M4): Sector Packs sind
 * Daten/Konfiguration nach einem gemeinsamen Schema — ein Framework, keine
 * Code-Forks. Packs enthalten Terminologie, Skill-Allowlists, Provider-Regeln,
 * Approval-Pflichten und Disclaimer pro Output-Typ. Alle Inhalte sind
 * generisch (keine echten Kundendaten, keine erfundenen Regulatorik-Behauptungen).
 */

export const SECTOR_PACK_IDS = ['finance', 'compliance', 'healthcare', 'construction'] as const

export const sectorPackIdSchema = z.enum(SECTOR_PACK_IDS)
export type SectorPackId = z.infer<typeof sectorPackIdSchema>

export const answerGuardsSchema = z
  .object({
    /** Every number in an answer needs a cited origin in a retrieved chunk. */
    numbers: z.boolean(),
    /** n-gram match against `quellenlage: primaer_lizenziert` chunk text. */
    verbatim: z.object({
      enabled: z.boolean(),
      /** Window width in words; 8 per M4 spec. */
      ngramSize: z.number().int().min(3).max(32),
    }),
    /** Applicable norm version is read from the generation matrix, never guessed. */
    normGeneration: z.boolean(),
  })
  .strict()

export type AnswerGuardsConfig = z.infer<typeof answerGuardsSchema>

export const sectorPackSchema = z
  .object({
    id: sectorPackIdSchema,
    version: z.string().min(1),
    /** Anzeigename (Deutsch) fuer Admin-/Settings-UI — Erweiterung zum Vertrags-Interface §4.1. */
    label: z.string().min(1).optional(),
    /** Begriffe → kanonische Bezeichnung. */
    terminology: z.record(z.string().min(1), z.string().min(1)),
    taxonomy: z.object({
      topics: z.array(z.string().min(1)),
      documentTypes: z.array(z.string().min(1)),
    }),
    /** Erlaubte/erkannte Quelltypen. */
    sourceTypes: z.array(z.string().min(1)),
    /** Freigegebene Connector-Typen. */
    connectors: z.array(z.string().min(1)),
    /** Skill-Allowlist (Built-in Skill-Namen wie 'blog', 'presentation', 'research'). */
    approvedSkills: z.array(z.string().min(1)),
    templates: z.array(
      z.object({
        outputType: z.string().min(1),
        templateRef: z.string().min(1),
      }),
    ),
    providerRules: z.object({
      euOnly: z.boolean(),
      allowedProviders: z.array(z.string().min(1)),
    }),
    /**
     * Answer guards (RNA M4): deterministic checks between retrieval and
     * answer output. Absent = no guards, so existing packs keep their
     * behaviour unchanged. Guards only ever withhold content; they never add,
     * calculate or round anything.
     */
    answerGuards: answerGuardsSchema.optional(),
    /** Output-Typen (TaskTypes) mit Approval-Pflicht. */
    requiredApprovals: z.array(z.string().min(1)),
    retentionDefaults: z.object({
      chatDays: z.number().int().positive(),
      auditDays: z.number().int().positive(),
    }),
    /** Disclaimer pro Output-Typ (z.B. keine Diagnose bei Healthcare). */
    disclaimers: z.array(
      z.object({
        outputType: z.string().min(1),
        text: z.string().min(1),
      }),
    ),
    goldenFixtures: z.array(
      z.object({
        scenario: z.string().min(1),
        fixtureRefs: z.array(z.string().min(1)),
        expect: z.string().min(1),
      }),
    ),
  })
  .strict()

export type SectorPack = z.infer<typeof sectorPackSchema>
